/**
 * Public landing page made for one cold-outreach prospect (TRUS-772).
 *
 * The first personalized email links here (/o/<token>). The page shows what
 * we noticed about their organization, where we could help, and three next
 * steps: book a call, ask a question, or not now. No login; the token is the
 * only key and the API returns nothing beyond their own organization and our
 * own copy.
 */
import React, { useEffect, useState } from 'react';
import { AlertCircle, CalendarClock, CheckCircle, Loader2, MessageSquare, PauseCircle } from 'lucide-react';
import { config as productConfig } from '@/src/config';
import { useAppTranslation } from '@/contexts/TranslationContext';
import DemandCards from './DemandCard';
import type { DemandCardData } from './demandCardLogic';

const API_BASE = (typeof window !== 'undefined' ? (productConfig.weaveinApi.baseUrl ?? '') : '').trim().replace(/\/$/, '');

interface LandingData {
  first_name?: string | null;
  organization?: string | null;
  headline?: string | null;
  intro?: string | null;
  next_steps_intro?: string | null;
  observations: string[];
  focus_areas: { title: string; why_it_matters: string; how_we_help: string }[];
  sender: { name?: string | null; organization?: string | null };
  booking_url?: string | null;
  responded?: 'book' | 'question' | 'not_now' | null;
  demand_cards?: DemandCardData[]; // TRUS-820, as of when the page was drafted
}

type Action = 'book' | 'question' | 'not_now';

export default function OutreachLandingPage({ token }: { token: string }) {
  const { t } = useAppTranslation();
  const [data, setData] = useState<LandingData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [asking, setAsking] = useState(false);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState<Action | null>(null);
  const [done, setDone] = useState<Action | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/api/public/outreach/${encodeURIComponent(token)}`)
      .then(async (res) => {
        if (!res.ok) throw new Error(res.status === 404 ? 'gone' : 'error');
        return res.json();
      })
      .then((json: LandingData) => {
        if (cancelled) return;
        setData(json);
        if (json.responded) setDone(json.responded);
        if (json.organization) document.title = `For ${json.organization}`;
      })
      .catch((e: Error) => !cancelled && setError(e.message));
    return () => {
      cancelled = true;
    };
  }, [token]);

  const respond = async (action: Action) => {
    setSending(action);
    setSendError(null);
    try {
      const res = await fetch(`${API_BASE}/api/public/outreach/${encodeURIComponent(token)}/respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, message: message.trim() || undefined }),
      });
      if (!res.ok) throw new Error();
      const json = await res.json();
      setDone(action);
      if (action === 'book' && json.booking_url) {
        window.open(json.booking_url, '_blank', 'noopener');
      }
    } catch {
      setSendError(t('outreachLanding.sendError', "We couldn't send that just now. Please try again, or reply to the email instead."));
    } finally {
      setSending(null);
    }
  };

  if (error) {
    return (
      <Shell>
        <div className="flex items-start gap-3 text-slate-700">
          <AlertCircle className="h-5 w-5 mt-0.5 text-slate-400" aria-hidden="true" />
          <p>
            {error === 'gone' ? t('outreachLanding.gone', "This page is no longer available. If you were expecting it, reply to the email and we will send a fresh link.") : t('outreachLanding.loadError', "This page could not load right now. Please try again in a moment.")}
          </p>
        </div>
      </Shell>
    );
  }

  if (!data) {
    return (
      <Shell>
        <p className="flex items-center gap-2 text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> {t('outreachLanding.loading', "Loading…")}
        </p>
      </Shell>
    );
  }

  const senderLine = [data.sender.name, data.sender.organization].filter(Boolean).join(' · ');

  return (
    <Shell>
      <header className="space-y-3">
        {data.organization && (
          <p className="text-xs font-semibold uppercase tracking-wider text-teal-700">
            {t('outreachLanding.preparedFor', "Prepared for {{organization}}", { organization: data.organization })}
          </p>
        )}
        <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900 leading-tight text-balance">
          {data.headline || t('outreachLanding.headlineFallback', "Ideas for {{organization}}", { organization: data.organization || t('outreachLanding.yourTeam', "your team") })}
        </h1>
        {data.intro && <p className="text-slate-600 leading-relaxed max-w-prose">{data.intro}</p>}
      </header>

      {data.observations.length > 0 && (
        <section className="mt-10">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500">{t('outreachLanding.noticed', "What we noticed")}</h2>
          <ul className="mt-3 space-y-2">
            {data.observations.map((o, i) => (
              <li key={i} className="flex gap-3 text-slate-700">
                <span className="mt-2 h-1.5 w-1.5 flex-none rounded-full bg-teal-600" aria-hidden="true" />
                <span>{o}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {data.focus_areas.length > 0 && (
        <section className="mt-10">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500">{t('outreachLanding.help', "Where we could help")}</h2>
          <div className="mt-3 grid gap-4 sm:grid-cols-2">
            {data.focus_areas.map((f, i) => (
              <article key={i} className="rounded-xl border border-slate-200 bg-white p-5">
                <h3 className="font-semibold text-slate-900">{f.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{f.why_it_matters}</p>
                <p className="mt-2 text-sm text-slate-800">{f.how_we_help}</p>
              </article>
            ))}
          </div>
        </section>
      )}

      {data.demand_cards && data.demand_cards.length > 0 && <DemandCards cards={data.demand_cards} />}

      <section className="mt-10 rounded-xl border border-slate-200 bg-white p-5 sm:p-6">
        {done ? (
          <div className="flex items-start gap-3">
            <CheckCircle className="h-5 w-5 mt-0.5 text-teal-600" aria-hidden="true" />
            <div className="text-slate-700">
              {done === 'book' && (
                <p>
                  {data.booking_url
                    ? t('outreachLanding.doneBookWithLink', "Thank you. Pick a time on the booking page, or {{sender}} will be in touch shortly to find a time.", { sender: data.sender.name || t('outreachLanding.we', "we") })
                    : t('outreachLanding.doneBook', "Thank you. {{sender}} will be in touch shortly to find a time.", { sender: data.sender.name || t('outreachLanding.we', "we") })}
                  {data.booking_url && (
                    <>
                      {' '}
                      <a href={data.booking_url} target="_blank" rel="noopener noreferrer" className="text-teal-700 underline">
                        {t('outreachLanding.openBooking', "Open the booking page")}
                      </a>
                    </>
                  )}
                </p>
              )}
              {done === 'question' && <p>{t('outreachLanding.doneQuestion', "Thank you, your question is on its way. Expect a reply by email soon.")}</p>}
              {done === 'not_now' && <p>{t('outreachLanding.doneNotNow', "Understood, thank you for letting us know. We won't follow up for now.")}</p>}
            </div>
          </div>
        ) : (
          <>
            <h2 className="font-semibold text-slate-900">{t('outreachLanding.nextSteps', "Next steps")}</h2>
            <p className="mt-1 text-sm text-slate-600">{data.next_steps_intro || t('outreachLanding.nextStepsFallback', "Pick whatever is most useful.")}</p>
            <div className="mt-4 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => respond('book')}
                disabled={sending !== null}
                className="inline-flex items-center gap-2 rounded-lg bg-teal-700 px-4 py-2.5 text-sm font-medium text-white hover:bg-teal-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-700 disabled:opacity-60"
              >
                {sending === 'book' ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarClock className="h-4 w-4" />}
                {t('outreachLanding.book', "Book a 20-minute call")}
              </button>
              <button
                type="button"
                onClick={() => setAsking((v) => !v)}
                disabled={sending !== null}
                className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-medium text-slate-800 hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-400 disabled:opacity-60"
              >
                <MessageSquare className="h-4 w-4" aria-hidden="true" />
                {t('outreachLanding.ask', "Ask a question")}
              </button>
              <button
                type="button"
                onClick={() => respond('not_now')}
                disabled={sending !== null}
                className="inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium text-slate-500 hover:text-slate-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-400 disabled:opacity-60"
              >
                {sending === 'not_now' ? <Loader2 className="h-4 w-4 animate-spin" /> : <PauseCircle className="h-4 w-4" />}
                {t('outreachLanding.notNow', "Not right now")}
              </button>
            </div>
            {asking && (
              <div className="mt-4">
                <label htmlFor="outreach-question" className="block text-sm font-medium text-slate-700">
                  {t('outreachLanding.yourQuestion', "Your question")}
                </label>
                <textarea
                  id="outreach-question"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={4}
                  maxLength={4000}
                  className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600"
                />
                <button
                  type="button"
                  onClick={() => respond('question')}
                  disabled={sending !== null || !message.trim()}
                  className="mt-2 inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
                >
                  {sending === 'question' && <Loader2 className="h-4 w-4 animate-spin" />}
                  {t('outreachLanding.sendQuestion', "Send question")}
                </button>
              </div>
            )}
            {sendError && <p className="mt-3 text-sm text-red-700">{sendError}</p>}
          </>
        )}
      </section>

      {senderLine && <p className="mt-8 text-sm text-slate-500">{t('outreachLanding.from', "From {{sender}}", { sender: senderLine })}</p>}
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-slate-50">
      <main className="mx-auto max-w-3xl px-4 py-12 sm:px-6 sm:py-16">{children}</main>
    </div>
  );
}
